// Utils
import { showErrorToast } from '@/utils/errorHandlers';

// Constants
import { WEBSOCKET_MESSAGE } from '@/constants';
import { WEBSOCKET_CONFIG } from '@/constants/config';

// Types
import { ActivityType } from '@shared/types';

// Types for WebSocket messages
export interface WebSocketMessage<T = unknown> {
  type: WebSocketMessageType;
  channel?: ActivityType;
  payload: T;
  clientId?: string;
}

export enum WebSocketMessageType {
  BROADCAST = 'broadcast',
  WELCOME = 'welcome',
}

export interface WebSocketConfig {
  url?: string;
  resolveUrl?: () => Promise<string | null | undefined>;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
}

export type WebSocketEventHandler<T = unknown> = (
  message: WebSocketMessage<T>,
) => void;

export class WebSocketService {
  private socket: WebSocket | null = null;
  private config: Required<WebSocketConfig>;
  private reconnectAttempts = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private isConnected = false;
  private isReconnecting = false;
  private connectPromise: Promise<void> | null = null;
  private isManualDisconnect = false;
  private eventHandlers: Set<WebSocketEventHandler> = new Set();

  constructor(config: WebSocketConfig) {
    const { RECONNECT_INTERVAL, MAX_RECONNECT_ATTEMPTS } = WEBSOCKET_CONFIG;

    this.config = {
      url: '',
      resolveUrl: async () => config.url,
      reconnectInterval: RECONNECT_INTERVAL,
      maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      onOpen: () => {},
      onClose: () => {},
      onError: () => {},
      ...config,
    };
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): Promise<void> {
    if (this.isSocketConnected()) {
      return Promise.resolve();
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.isManualDisconnect = false;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    this.connectPromise = new Promise((resolve, reject) => {
      this.setupConnection(resolve, reject).catch((error) => {
        this.isConnected = false;
        this.isReconnecting = false;
        this.connectPromise = null;
        reject(error);
      });
    });

    return this.connectPromise;
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.isManualDisconnect = true;
    this.isReconnecting = false;
    this.connectPromise = null;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    this.closeSocket();
    this.isConnected = false;
  }

  /**
   * Subscribe to WebSocket events
   */
  subscribe(handler: WebSocketEventHandler): () => void {
    this.eventHandlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  /**
   * Send a message through the WebSocket
   */
  send(message: Record<string, unknown>): void {
    if (this.socket && this.isConnected) {
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Get connection status
   */
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.readyState === WebSocket.OPEN;
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(message: WebSocketMessage): void {
    // Notify all subscribers
    this.eventHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (_) {
        showErrorToast(WEBSOCKET_MESSAGE.HANDLER_ERROR);
      }
    });
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (
      this.isReconnecting ||
      this.reconnectAttempts >= this.config.maxReconnectAttempts
    ) {
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    const delay = Math.min(
      this.config.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      30000, // Max 30 seconds
    );

    this.reconnectTimeoutId = setTimeout(async () => {
      try {
        await this.connect();
      } catch (_) {
        this.isReconnecting = false;
        this.attemptReconnect();
      }
    }, delay);
  }

  private async setupConnection(
    resolve: () => void,
    reject: (reason?: unknown) => void,
  ): Promise<void> {
    const resolvedUrl = await this.config.resolveUrl();
    const url = resolvedUrl ?? this.config.url;

    if (!url) {
      this.isReconnecting = false;
      this.connectPromise = null;
      resolve();
      return;
    }

    this.closeSocket();

    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) {
        return;
      }

      this.isConnected = true;
      this.isReconnecting = false;
      this.reconnectAttempts = 0;
      this.connectPromise = null;
      this.config.onOpen();
      resolve();
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) {
        return;
      }

      const message: WebSocketMessage = JSON.parse(event.data) || {};

      this.handleMessage(message);
    };

    socket.onclose = () => {
      if (this.socket === socket) {
        this.socket = null;
      }

      this.isConnected = false;
      this.connectPromise = null;
      this.config.onClose();

      if (!this.isManualDisconnect) {
        this.attemptReconnect();
      }
    };

    socket.onerror = (error) => {
      if (this.socket !== socket) {
        return;
      }

      this.isConnected = false;
      this.connectPromise = null;
      this.config.onError(error);
      reject(error);
    };
  }

  private closeSocket(): void {
    if (!this.socket) {
      return;
    }

    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onclose = null;
    this.socket.onerror = null;
    this.socket.close();
    this.socket = null;
  }
}

/**
 * Create a new WebSocket service instance
 * @param config - WebSocket configuration including URL
 * @returns A new WebSocketService instance
 *
 * @example
 * // Create multiple connections for different URLs
 * const personalInfoWS = createWebSocketService({ url: 'wss://api1.example.com' });
 * const chatWS = createWebSocketService({ url: 'wss://api2.example.com' });
 *
 * await personalInfoWS.connect();
 * await chatWS.connect();
 */
export const createWebSocketService = (
  config: WebSocketConfig,
): WebSocketService => {
  return new WebSocketService(config);
};
